import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  LogOut,
  Plus,
  Trash2,
  Edit3,
  RefreshCw,
  PackageCheck,
  FileText,
  ExternalLink,
  ArrowRightLeft,
  Search,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { adminApi, clearToken, getToken, ORDER_STATUSES } from '@/lib/adminApi';

const STATUS_COLOR = {
  'Order Placed': 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  'Picked Up': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40',
  'In Transit': 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  'Out for Delivery': 'bg-orange-500/20 text-orange-300 border-orange-500/40',
  Delivered: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  Cancelled: 'bg-rose-500/20 text-rose-300 border-rose-500/40',
  pending: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  contacted: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  converted: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  rejected: 'bg-rose-500/20 text-rose-300 border-rose-500/40',
};

const EMPTY_ORDER = {
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  pickup_location: '',
  delivery_location: '',
  weight: '',
  dimensions: '',
  description: '',
};

function StatusBadge({ status }) {
  const cls = STATUS_COLOR[status] || 'bg-muted/40 text-muted-foreground border-border';
  return (
    <Badge variant="outline" className={`${cls} font-medium`}>
      {status}
    </Badge>
  );
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderSearch, setOrderSearch] = useState('');
  const [quoteSearch, setQuoteSearch] = useState('');

  // Create order modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_ORDER);
  const [createSubmitting, setCreateSubmitting] = useState(false);

  // Edit order modal
  const [editOpen, setEditOpen] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editStatusMeta, setEditStatusMeta] = useState({ status_location: '', status_notes: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Delete confirm
  const [confirm, setConfirm] = useState(null); // { type, id, label }

  // Quote detail modal
  const [quoteDetail, setQuoteDetail] = useState(null);

  const handleLogout = useCallback(() => {
    clearToken();
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [o, q] = await Promise.all([adminApi.listOrders(), adminApi.listQuotes()]);
      setOrders(o);
      setQuotes(q);
    } catch (err) {
      if (err.status === 401) {
        handleLogout();
      } else {
        toast.error(err.message || 'Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin/login', { replace: true });
      return;
    }
    adminApi
      .me()
      .then((u) => {
        setUser(u);
        loadAll();
      })
      .catch(() => handleLogout());
  }, [navigate, handleLogout, loadAll]);

  // -------- Order: Create --------
  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.customer_name || !createForm.customer_email || !createForm.pickup_location || !createForm.delivery_location) {
      toast.error('Please fill all required fields');
      return;
    }
    setCreateSubmitting(true);
    try {
      const payload = {
        ...createForm,
        weight: createForm.weight ? parseFloat(createForm.weight) : null,
      };
      const newOrder = await adminApi.createOrder(payload);
      toast.success(`Order created: ${newOrder.tracking_number}`);
      setCreateOpen(false);
      setCreateForm(EMPTY_ORDER);
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Failed to create order');
    } finally {
      setCreateSubmitting(false);
    }
  };

  // -------- Order: Edit --------
  const openEdit = (order) => {
    setEditOrder(order);
    setEditForm({
      customer_name: order.customer_name || '',
      customer_email: order.customer_email || '',
      customer_phone: order.customer_phone || '',
      pickup_location: order.pickup_location || '',
      delivery_location: order.delivery_location || '',
      weight: order.weight != null ? String(order.weight) : '',
      dimensions: order.dimensions || '',
      description: order.description || '',
      status: order.status || 'Order Placed',
    });
    setEditStatusMeta({ status_location: '', status_notes: '' });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    try {
      const payload = {
        ...editForm,
        weight: editForm.weight ? parseFloat(editForm.weight) : null,
      };
      if (editForm.status !== editOrder.status) {
        if (editStatusMeta.status_location) payload.status_location = editStatusMeta.status_location;
        if (editStatusMeta.status_notes) payload.status_notes = editStatusMeta.status_notes;
      }
      await adminApi.updateOrder(editOrder.id, payload);
      toast.success('Order updated');
      setEditOpen(false);
      setEditOrder(null);
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Failed to update order');
    } finally {
      setEditSubmitting(false);
    }
  };

  // -------- Delete --------
  const performDelete = async () => {
    if (!confirm) return;
    try {
      if (confirm.type === 'order') {
        await adminApi.deleteOrder(confirm.id);
        toast.success('Order deleted');
      } else if (confirm.type === 'quote') {
        await adminApi.deleteQuote(confirm.id);
        toast.success('Quote deleted');
      }
      setConfirm(null);
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  // -------- Quote actions --------
  const convertQuote = async (quoteId) => {
    try {
      const order = await adminApi.convertQuote(quoteId);
      toast.success(`Converted → order ${order.tracking_number}`);
      setQuoteDetail(null);
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Failed to convert');
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await adminApi.updateQuoteStatus(quoteId, status);
      toast.success('Quote status updated');
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  };

  // -------- Filtering --------
  const filteredOrders = orders.filter((o) => {
    if (!orderSearch.trim()) return true;
    const q = orderSearch.toLowerCase();
    return (
      o.tracking_number?.toLowerCase().includes(q) ||
      o.customer_name?.toLowerCase().includes(q) ||
      o.customer_email?.toLowerCase().includes(q) ||
      o.status?.toLowerCase().includes(q)
    );
  });
  const filteredQuotes = quotes.filter((q) => {
    if (!quoteSearch.trim()) return true;
    const s = quoteSearch.toLowerCase();
    return (
      q.name?.toLowerCase().includes(s) ||
      q.email?.toLowerCase().includes(s) ||
      q.company?.toLowerCase().includes(s) ||
      q.status?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="min-h-screen bg-background relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[400px] bg-primary/5 blur-[140px] rounded-full pointer-events-none" />

      {/* Top bar */}
      <header className="border-b border-border/50 bg-card/60 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_79ae8060-e1f8-4d28-b152-a3397f858fee/artifacts/8r1orql6_syncline%20logistics%20logo.png"
                alt="Syncline Logistics"
                className="h-10 w-auto"
              />
            </Link>
            <div className="hidden sm:block">
              <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10">
                Admin
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <span className="text-sm text-muted-foreground hidden md:inline">
                Signed in as <span className="text-foreground font-medium">{user.username}</span>
              </span>
            )}
            <Button
              data-testid="admin-refresh-btn"
              variant="outline"
              size="sm"
              onClick={loadAll}
              disabled={loading}
              className="border-border/60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              data-testid="admin-logout-btn"
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-border/60"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 relative z-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-5 bg-card/70 backdrop-blur-sm border-border/50">
            <div className="text-sm text-muted-foreground">Total Orders</div>
            <div className="text-3xl font-bold text-gradient mt-1">{orders.length}</div>
          </Card>
          <Card className="p-5 bg-card/70 backdrop-blur-sm border-border/50">
            <div className="text-sm text-muted-foreground">In Transit</div>
            <div className="text-3xl font-bold text-amber-300 mt-1">
              {orders.filter((o) => o.status === 'In Transit' || o.status === 'Out for Delivery').length}
            </div>
          </Card>
          <Card className="p-5 bg-card/70 backdrop-blur-sm border-border/50">
            <div className="text-sm text-muted-foreground">Delivered</div>
            <div className="text-3xl font-bold text-emerald-300 mt-1">
              {orders.filter((o) => o.status === 'Delivered').length}
            </div>
          </Card>
          <Card className="p-5 bg-card/70 backdrop-blur-sm border-border/50">
            <div className="text-sm text-muted-foreground">Pending Quotes</div>
            <div className="text-3xl font-bold text-primary mt-1">
              {quotes.filter((q) => q.status === 'pending').length}
            </div>
          </Card>
        </div>

        <Tabs defaultValue="orders" className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <TabsList className="bg-card/70 border border-border/50">
              <TabsTrigger data-testid="tab-orders" value="orders" className="gap-2">
                <PackageCheck className="h-4 w-4" /> Orders ({orders.length})
              </TabsTrigger>
              <TabsTrigger data-testid="tab-quotes" value="quotes" className="gap-2">
                <FileText className="h-4 w-4" /> Quotes ({quotes.length})
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ORDERS TAB */}
          <TabsContent value="orders">
            <Card className="p-4 sm:p-6 bg-card/70 backdrop-blur-sm border-border/50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="orders-search"
                    placeholder="Search by tracking #, customer, status…"
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    className="pl-9 bg-background border-border/50"
                  />
                </div>
                <Button
                  data-testid="create-order-btn"
                  onClick={() => setCreateOpen(true)}
                  className="bg-gradient-primary hover:shadow-glow"
                >
                  <Plus className="h-4 w-4 mr-2" /> Create Order
                </Button>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <TableHead>Tracking #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-12">
                          {loading ? 'Loading…' : 'No orders found'}
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredOrders.map((order) => (
                      <TableRow
                        key={order.id}
                        data-testid={`order-row-${order.id}`}
                        className="border-border/50"
                      >
                        <TableCell className="font-mono text-sm text-primary">
                          {order.tracking_number}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{order.customer_name}</div>
                          <div className="text-xs text-muted-foreground">{order.customer_email}</div>
                        </TableCell>
                        <TableCell className="text-sm max-w-xs">
                          <div className="truncate">{order.pickup_location}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            → {order.delivery_location}
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={order.status} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(order.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              data-testid={`edit-order-btn-${order.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => openEdit(order)}
                              className="border-border/60"
                            >
                              <Edit3 className="h-3.5 w-3.5" />
                            </Button>
                            <a
                              href={`/track?tn=${order.tracking_number}`}
                              target="_blank"
                              rel="noreferrer"
                              title="View public tracking"
                            >
                              <Button variant="outline" size="sm" className="border-border/60">
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            </a>
                            <Button
                              data-testid={`delete-order-btn-${order.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setConfirm({ type: 'order', id: order.id, label: order.tracking_number })
                              }
                              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* QUOTES TAB */}
          <TabsContent value="quotes">
            <Card className="p-4 sm:p-6 bg-card/70 backdrop-blur-sm border-border/50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="quotes-search"
                    placeholder="Search quotes…"
                    value={quoteSearch}
                    onChange={(e) => setQuoteSearch(e.target.value)}
                    className="pl-9 bg-background border-border/50"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <TableHead>Customer</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuotes.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-12">
                          {loading ? 'Loading…' : 'No quotes found'}
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredQuotes.map((quote) => (
                      <TableRow
                        key={quote.id}
                        data-testid={`quote-row-${quote.id}`}
                        className="border-border/50"
                      >
                        <TableCell>
                          <div className="font-medium">{quote.name}</div>
                          <div className="text-xs text-muted-foreground">{quote.email}</div>
                          {quote.company && (
                            <div className="text-xs text-muted-foreground">{quote.company}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm max-w-xs">
                          <div className="truncate">{quote.pickup_location}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            → {quote.delivery_location}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>Qty: {quote.quantity}</div>
                          {quote.weight && (
                            <div className="text-xs text-muted-foreground">
                              {quote.weight} lbs
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={quote.status}
                            onValueChange={(v) => updateQuoteStatus(quote.id, v)}
                          >
                            <SelectTrigger
                              data-testid={`quote-status-select-${quote.id}`}
                              className="w-36 h-8 bg-background border-border/50 text-xs"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="converted">Converted</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(quote.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              data-testid={`view-quote-btn-${quote.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => setQuoteDetail(quote)}
                              className="border-border/60"
                            >
                              View
                            </Button>
                            {quote.status !== 'converted' && (
                              <Button
                                data-testid={`convert-quote-btn-${quote.id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => convertQuote(quote.id)}
                                className="border-primary/40 text-primary hover:bg-primary/10"
                              >
                                <ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Convert
                              </Button>
                            )}
                            <Button
                              data-testid={`delete-quote-btn-${quote.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setConfirm({ type: 'quote', id: quote.id, label: quote.name })
                              }
                              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Order Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent
          data-testid="create-order-dialog"
          className="max-w-2xl bg-card border-border/50 max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>Create New Order</DialogTitle>
            <DialogDescription>
              A tracking number will be auto-generated. The order will start with status “Order Placed”.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer Name *</Label>
                <Input
                  data-testid="create-customer-name"
                  value={createForm.customer_name}
                  onChange={(e) => setCreateForm({ ...createForm, customer_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Customer Email *</Label>
                <Input
                  data-testid="create-customer-email"
                  type="email"
                  value={createForm.customer_email}
                  onChange={(e) => setCreateForm({ ...createForm, customer_email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  data-testid="create-customer-phone"
                  value={createForm.customer_phone}
                  onChange={(e) => setCreateForm({ ...createForm, customer_phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Weight (lbs)</Label>
                <Input
                  data-testid="create-weight"
                  type="number"
                  step="0.01"
                  value={createForm.weight}
                  onChange={(e) => setCreateForm({ ...createForm, weight: e.target.value })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Pickup Location *</Label>
                <Input
                  data-testid="create-pickup"
                  value={createForm.pickup_location}
                  onChange={(e) => setCreateForm({ ...createForm, pickup_location: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Delivery Location *</Label>
                <Input
                  data-testid="create-delivery"
                  value={createForm.delivery_location}
                  onChange={(e) => setCreateForm({ ...createForm, delivery_location: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Dimensions</Label>
                <Input
                  data-testid="create-dimensions"
                  placeholder="L x W x H"
                  value={createForm.dimensions}
                  onChange={(e) => setCreateForm({ ...createForm, dimensions: e.target.value })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Description</Label>
                <Textarea
                  data-testid="create-description"
                  rows={3}
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="create-order-submit"
                disabled={createSubmitting}
                className="bg-gradient-primary"
              >
                {createSubmitting ? 'Creating…' : 'Create Order'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent
          data-testid="edit-order-dialog"
          className="max-w-2xl bg-card border-border/50 max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>
              Edit Order{' '}
              <span className="font-mono text-primary text-base">
                {editOrder?.tracking_number}
              </span>
            </DialogTitle>
            <DialogDescription>
              Update details or move the order through its lifecycle. Status changes are added to the
              public tracking timeline.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <form onSubmit={submitEdit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Status</Label>
                  <Select
                    value={editForm.status}
                    onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                  >
                    <SelectTrigger data-testid="edit-status-select" className="bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ORDER_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {editForm.status !== editOrder?.status && (
                  <>
                    <div className="space-y-2">
                      <Label>Status Location (optional)</Label>
                      <Input
                        data-testid="edit-status-location"
                        placeholder="e.g., Toronto Hub"
                        value={editStatusMeta.status_location}
                        onChange={(e) =>
                          setEditStatusMeta({ ...editStatusMeta, status_location: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Status Notes (optional)</Label>
                      <Input
                        data-testid="edit-status-notes"
                        placeholder="Note shown to customer"
                        value={editStatusMeta.status_notes}
                        onChange={(e) =>
                          setEditStatusMeta({ ...editStatusMeta, status_notes: e.target.value })
                        }
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Customer Name</Label>
                  <Input
                    value={editForm.customer_name}
                    onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Customer Email</Label>
                  <Input
                    type="email"
                    value={editForm.customer_email}
                    onChange={(e) => setEditForm({ ...editForm, customer_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={editForm.customer_phone}
                    onChange={(e) => setEditForm({ ...editForm, customer_phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Weight (lbs)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.weight}
                    onChange={(e) => setEditForm({ ...editForm, weight: e.target.value })}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Pickup Location</Label>
                  <Input
                    value={editForm.pickup_location}
                    onChange={(e) => setEditForm({ ...editForm, pickup_location: e.target.value })}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Delivery Location</Label>
                  <Input
                    value={editForm.delivery_location}
                    onChange={(e) => setEditForm({ ...editForm, delivery_location: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dimensions</Label>
                  <Input
                    value={editForm.dimensions}
                    onChange={(e) => setEditForm({ ...editForm, dimensions: e.target.value })}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    rows={3}
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  />
                </div>
              </div>

              {editOrder?.history?.length > 0 && (
                <div className="border-t border-border/50 pt-4">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Status History
                  </Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto pr-2">
                    {editOrder.history.map((h, idx) => (
                      <div key={idx} className="text-sm flex items-start gap-2">
                        <Badge variant="outline" className="border-border/50 text-xs">
                          {h.status}
                        </Badge>
                        <div className="flex-1">
                          {h.location && (
                            <div className="text-xs text-muted-foreground">{h.location}</div>
                          )}
                          {h.description && (
                            <div className="text-xs text-muted-foreground">{h.description}</div>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(h.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="edit-order-submit"
                  disabled={editSubmitting}
                  className="bg-gradient-primary"
                >
                  {editSubmitting ? 'Saving…' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Quote detail Dialog */}
      <Dialog open={!!quoteDetail} onOpenChange={(o) => !o && setQuoteDetail(null)}>
        <DialogContent
          data-testid="quote-detail-dialog"
          className="max-w-xl bg-card border-border/50"
        >
          <DialogHeader>
            <DialogTitle>Quote from {quoteDetail?.name}</DialogTitle>
            <DialogDescription>
              Submitted {formatDate(quoteDetail?.created_at)}
            </DialogDescription>
          </DialogHeader>
          {quoteDetail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email" value={quoteDetail.email} />
                <Field label="Phone" value={quoteDetail.phone} />
                <Field label="Company" value={quoteDetail.company || '—'} />
                <Field
                  label="Pickup Date"
                  value={quoteDetail.pickup_date || 'Not specified'}
                />
                <Field label="Pickup" value={quoteDetail.pickup_location} />
                <Field label="Delivery" value={quoteDetail.delivery_location} />
                <Field label="Quantity" value={String(quoteDetail.quantity)} />
                <Field
                  label="Total Weight"
                  value={quoteDetail.weight ? `${quoteDetail.weight} lbs` : '—'}
                />
              </div>
              {quoteDetail.dimensions && (
                <Field label="Dimensions" value={quoteDetail.dimensions} block />
              )}
              {quoteDetail.description && (
                <Field label="Description" value={quoteDetail.description} block />
              )}
              <div className="flex items-center justify-between pt-3 border-t border-border/50">
                <StatusBadge status={quoteDetail.status} />
                {quoteDetail.status !== 'converted' && (
                  <Button
                    onClick={() => convertQuote(quoteDetail.id)}
                    className="bg-gradient-primary"
                  >
                    <ArrowRightLeft className="h-4 w-4 mr-2" /> Convert to Order
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-card border-border/50">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirm?.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong className="text-foreground">{confirm?.label}</strong>
              {confirm?.type === 'order' ? ' and its tracking history' : ''}. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={performDelete}
              className="bg-rose-500 hover:bg-rose-600 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, value, block }) {
  return (
    <div className={block ? 'col-span-2' : ''}>
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="font-medium break-words">{value}</div>
    </div>
  );
}
